import { supabase } from './supabase';
import type { TimeSlot } from '../stores/useSchedulingStore';

/** YYYY-MM-DD as local calendar day (avoid Date.parse / UTC midnight shift). */
export function parseYmdLocal(ymd: string): Date {
  const [y, m, d] = ymd.trim().split('-').map((x) => parseInt(x, 10));
  if (!y || !m || !d) return new Date(NaN);
  return new Date(y, m - 1, d);
}

function dateToLocalYmd(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * Calculate available time slots for a given date
 */
export async function calculateAvailability(
  empresaId: string,
  date: string
): Promise<TimeSlot[]> {
  try {
    // Get scheduling settings
    const { data: settings, error: settingsError } = await supabase
      .from('scheduling_settings')
      .select('*')
      .eq('empresa_id', empresaId)
      .maybeSingle();

    if (settingsError && settingsError.code !== 'PGRST116') {
      throw settingsError;
    }

    if (!settings) {
      // No settings configured, return empty
      return [];
    }

    // Check if date is a holiday
    const { data: holiday } = await supabase
      .from('scheduling_holidays')
      .select('*')
      .eq('empresa_id', empresaId)
      .eq('holiday_date', date)
      .maybeSingle();

    if (holiday?.is_closed) {
      return []; // No availability on closed holidays
    }

    // Get day of week (date is YYYY-MM-DD local)
    const dateObj = parseYmdLocal(date);
    const dayIndex = dateObj.getDay();
    const dayOfWeek = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'][dayIndex] as string;
    const operatingHours = settings.operating_hours as Record<string, any>;
    const dayHours = operatingHours[dayOfWeek];

    // Use holiday custom hours if available, otherwise use day hours
    const hours = holiday?.custom_hours ? {
      open: holiday.custom_hours.open,
      close: holiday.custom_hours.close,
      last_booking: holiday.custom_hours.last_booking,
      is_open: true,
    } : dayHours;

    if (!hours || !hours.is_open || !hours.open || !hours.close) {
      return []; // Closed day
    }

    // Check min lead time (don't show slots too soon)
    const now = new Date();
    const appointmentDate = parseYmdLocal(date);
    const minLeadTime = settings.min_lead_time_hours || 2;
    const minDateTime = new Date(now.getTime() + minLeadTime * 60 * 60 * 1000);

    const todayLocal = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    if (appointmentDate < todayLocal) {
      return []; // Past date
    }

    // Check max booking days advance
    const maxDays = settings.max_booking_days_advance || 30;
    const maxDate = new Date(todayLocal);
    maxDate.setDate(maxDate.getDate() + maxDays);
    if (appointmentDate > maxDate) {
      return []; // Too far in advance
    }

    // Generate time slots
    const slots: TimeSlot[] = [];
    const [openHour, openMin] = hours.open.split(':').map(Number);
    const [_closeHour, _closeMin] = hours.close.split(':').map(Number);
    const lastBookingTime = hours.last_booking || hours.close;
    const [lastBookingHour, lastBookingMin] = lastBookingTime.split(':').map(Number);

    const slotDuration = settings.slot_duration_minutes || 30;
    const totalSlots = settings.slots_per_interval || 5;

    let currentHour = openHour;
    let currentMin = openMin;

    while (
      currentHour < lastBookingHour ||
      (currentHour === lastBookingHour && currentMin <= lastBookingMin)
    ) {
      const timeStr = `${String(currentHour).padStart(2, '0')}:${String(currentMin).padStart(2, '0')}`;
      
      // Check if this slot is too soon (min lead time)
      const slotDateTime = new Date(`${date}T${timeStr}:00`);
      if (
        slotDateTime < minDateTime &&
        appointmentDate.getTime() === todayLocal.getTime()
      ) {
        // Skip this slot if it's too soon today
        currentMin += slotDuration;
        if (currentMin >= 60) {
          currentHour += Math.floor(currentMin / 60);
          currentMin = currentMin % 60;
        }
        continue;
      }
      
      // Count existing appointments for this time slot
      const { count } = await supabase
        .from('appointments')
        .select('*', { count: 'exact', head: true })
        .eq('empresa_id', empresaId)
        .eq('appointment_date', date)
        .eq('appointment_time', timeStr)
        .in('status', ['pending', 'confirmed']);

      const bookedCount = count || 0;
      const availableCount = Math.max(0, totalSlots - bookedCount);

      slots.push({
        time: timeStr,
        available: availableCount > 0,
        availableCount,
        totalSlots,
      });

      // Move to next slot
      currentMin += slotDuration;
      if (currentMin >= 60) {
        currentHour += Math.floor(currentMin / 60);
        currentMin = currentMin % 60;
      }
    }

    return slots;
  } catch (error: any) {
    console.error('Error calculating availability:', error);
    throw error;
  }
}

/**
 * Get available dates within booking window
 */
export async function getAvailableDates(
  empresaId: string,
  startDate: Date = new Date(),
  days: number = 30
): Promise<string[]> {
  try {
    const { data: settings } = await supabase
      .from('scheduling_settings')
      .select('*')
      .eq('empresa_id', empresaId)
      .maybeSingle();

    if (!settings) {
      return [];
    }

    const maxDays = settings.max_booking_days_advance || 30;
    const actualDays = Math.min(days, maxDays);

    const availableDates: string[] = [];
    const { data: holidaysData } = await supabase
      .from('scheduling_holidays')
      .select('holiday_date, is_closed')
      .eq('empresa_id', empresaId)
      .gte('holiday_date', dateToLocalYmd(startDate));

    const holidayDates = new Set(
      (holidaysData || [])
        .filter(h => h.is_closed && h.holiday_date)
        .map(h => h.holiday_date as string)
    );

    const operatingHours = settings.operating_hours as Record<string, any>;
    
    for (let i = 0; i < actualDays; i++) {
      const date = new Date(startDate);
      date.setDate(date.getDate() + i);
      const dateStr = dateToLocalYmd(date);
      
      // Skip if it's a closed holiday
      if (holidayDates.has(dateStr)) {
        continue;
      }

      // Check if day is open
      const dayIndex = date.getDay();
      const dayOfWeek = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'][dayIndex] as string;
      const dayHours = operatingHours[dayOfWeek];
      
      if (dayHours?.is_open && dayHours.open && dayHours.close) {
        availableDates.push(dateStr);
      }
    }

    return availableDates;
  } catch (error: any) {
    console.error('Error getting available dates:', error);
    return [];
  }
}

/** Compact horario de atención desde scheduling_settings (sin cupos por hora). */
async function getOperatingHoursSummary(empresaId: string): Promise<string | null> {
  const { data: settings } = await supabase
    .from('scheduling_settings')
    .select('operating_hours')
    .eq('empresa_id', empresaId)
    .maybeSingle();
  if (!settings?.operating_hours || typeof settings.operating_hours !== 'object') return null;

  const labels: Record<string, string> = {
    monday: 'Lun',
    tuesday: 'Mar',
    wednesday: 'Mié',
    thursday: 'Jue',
    friday: 'Vie',
    saturday: 'Sáb',
    sunday: 'Dom',
  };
  const parts: string[] = [];
  for (const key of ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const) {
    const day = (settings.operating_hours as Record<string, any>)[key];
    if (day?.is_open && day.open && day.close) {
      parts.push(`${labels[key]} ${day.open}–${day.close}`);
    }
  }
  if (parts.length === 0) return null;
  return `Horario orientativo de la tienda (citas / retiros): ${parts.join(', ')}. Los cupos por hora pueden reservarse desde el calendario del formulario si la tienda lo activó.`;
}

/**
 * Resumen de agenda para el chat público (misma lógica que CalendarPicker).
 * Solo texto para el system prompt del asistente; cupos pueden cambiar al instante.
 */
export async function getChatAvailabilitySummary(empresaId: string): Promise<string> {
  try {
    const hoursLine = await getOperatingHoursSummary(empresaId);
    const dates = await getAvailableDates(empresaId, new Date(), 21);
    if (!dates.length) {
      const base =
        'No hay fechas con cupos disponibles en la ventana próxima (o la reserva por hora no está activa). ';
      const hint =
        'Si el cliente pregunta por citas o entregas programadas, sugiere el botón "Agendar" del chat o contacto directo con la tienda.';
      return hoursLine ? `${hoursLine}\n\n${base}${hint}` : `${base}${hint}`;
    }

    const lines: string[] = [];
    const maxDaysToDetail = 5;
    const toCheck = dates.slice(0, maxDaysToDetail);

    for (const d of toCheck) {
      const slots = await calculateAvailability(empresaId, d);
      const open = slots.filter((s) => s.available).map((s) => s.time);
      if (open.length === 0) continue;
      const preview = open.slice(0, 10).join(', ');
      const extra = open.length > 10 ? ` …y ${open.length - 10} horarios más ese día` : '';
      lines.push(`${d}: ${preview}${extra}`);
    }

    if (lines.length === 0) {
      const slotMsg =
        'El calendario de la tienda tiene días hábiles pero en las próximas fechas consultadas los cupos por hora pueden estar completos. ' +
        'Sugiere abrir el botón "Agendar" del panel para ver horarios actualizados y reservar.';
      return hoursLine ? `${hoursLine}\n\n${slotMsg}` : slotMsg;
    }

    const slotsBlock =
      'Disponibilidad orientativa (fechas en formato YYYY-MM-DD, hora local de la tienda). Los cupos cambian en tiempo real; la reserva firme solo en el formulario web:\n' +
      lines.map((l) => `- ${l}`).join('\n');
    return hoursLine ? `${hoursLine}\n\n${slotsBlock}` : slotsBlock;
  } catch (e) {
    console.error('[getChatAvailabilitySummary]', e);
    return (
      'No se pudo consultar la agenda en este momento. Para ver citas disponibles, el cliente debe usar el botón "Agendar" del panel del chat.'
    );
  }
}
